import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Action, ErrorNote, FilterChips, Panel, PanelHead } from '../aa';
import { Sparkline } from '../../pages/owner/dashboard/primitives';
import { billingApi, type PeopleUsage, type PersonUsageRow, type UsagePeriod } from '../../services/billingApi';
import { Avatar, PERIODS, displayName, lastSeen, roleLabel, sinceLabel, usd } from './usageParts';

const SHOWN = 6;
const SHARE_TONES = ['#18191D', '#3A3C42', '#666971', '#92949D', '#B9BBC1'];
const OTHERS_TONE = '#E4E5E2';

export const personPath = (person: { key: string }) => `/usage/${encodeURIComponent(person.key)}`;

/* Who the company's usage came from. One bar splits the total between the
 * biggest spenders; each row opens that person's own usage page. */
const ShareStrip: React.FC<{ people: PersonUsageRow[]; total: number }> = ({ people, total }) => {
  const top = people.filter((p) => p.total_usd > 0).slice(0, SHARE_TONES.length);
  const rest = Math.max(total - top.reduce((s, p) => s + p.total_usd, 0), 0);
  if (top.length === 0) return null;
  const parts = top.map((p, i) => ({ label: displayName(p), value: p.total_usd, tone: SHARE_TONES[i] }));
  if (rest > 0.000001) parts.push({ label: 'Everyone else', value: rest, tone: OTHERS_TONE });
  return (
    <div>
      <div
        className="flex h-3 overflow-hidden rounded-full bg-canvas-inset"
        role="img"
        aria-label={`Share of usage: ${parts.map((p) => `${p.label} ${Math.round((p.value / total) * 100)}%`).join(', ')}`}
      >
        {parts.map((p) => (
          <span
            key={p.label}
            className="h-full border-r-2 border-white last:border-r-0"
            style={{ width: `${(p.value / total) * 100}%`, background: p.tone }}
            title={`${p.label}: ${usd(p.value)}`}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {parts.map((p) => (
          <span key={p.label} className="inline-flex items-center gap-1.5 text-[11.5px] text-ink-muted">
            <i className="h-2.5 w-2.5 rounded-[3px] ring-1 ring-inset ring-black/10" style={{ background: p.tone }} />
            {p.label}
            <b className="font-semibold tabular-nums text-ink">{Math.round((p.value / total) * 100)}%</b>
          </span>
        ))}
      </div>
    </div>
  );
};

const PersonRow: React.FC<{ person: PersonUsageRow; onOpen: () => void }> = ({ person, onOpen }) => {
  const idle = person.calls === 0 && person.total_usd === 0;
  let activity = `${person.calls} ${person.calls === 1 ? 'call' : 'calls'}`;
  if (idle) activity = 'No usage yet';
  return (
    <li className="border-b border-line-soft last:border-b-0">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${displayName(person)}, ${usd(person.total_usd)}, ${activity}. Open their usage`}
        className="grid min-h-[64px] w-full grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-3 px-6 py-3 text-left transition-colors hover:bg-canvas focus-visible:bg-canvas focus-visible:outline-none sm:grid-cols-[auto_minmax(0,1fr)_120px_96px_auto_auto]"
      >
        <Avatar person={person} />
        <span className="min-w-0">
          <span className="block truncate text-[13.5px] font-semibold text-ink">{displayName(person)}</span>
          <span className="block truncate text-[12px] text-ink-dim">{roleLabel(person)}</span>
        </span>
        <span className="hidden sm:block">
          <span className="flex h-1.5 overflow-hidden rounded-full bg-canvas-inset">
            <span className="h-full rounded-full bg-ink" style={{ width: `${Math.max(person.share * 100, person.share > 0 ? 3 : 0)}%` }} />
          </span>
          <span className="mt-1 block text-[11px] tabular-nums text-ink-dim">{Math.round(person.share * 100)}% of usage</span>
        </span>
        <span className="hidden sm:block">
          {person.trend.some((v) => v > 0) && <Sparkline points={person.trend} label={`${displayName(person)}, last 14 days`} />}
          {!person.trend.some((v) => v > 0) && <span className="text-[11px] text-ink-soft">Quiet for 14 days</span>}
        </span>
        <span className="text-right">
          <span className="block text-[14px] font-semibold tabular-nums text-ink">{usd(person.total_usd)}</span>
          <span className="block whitespace-nowrap text-[11.5px] text-ink-dim">{idle ? lastSeen(person.last_at) : activity}</span>
        </span>
        <ChevronRight size={16} aria-hidden className="text-ink-soft" />
      </button>
    </li>
  );
};

const SkeletonList: React.FC = () => (
  <div aria-busy="true" aria-label="Loading usage by person" className="px-6 pb-6">
    <div className="h-3 animate-pulse rounded-full bg-canvas-inset" />
    {[0, 1, 2].map((i) => (
      <div key={i} className="mt-4 flex items-center gap-3">
        <span className="h-9 w-9 animate-pulse rounded-full bg-canvas-inset" />
        <span className="flex-1 space-y-2">
          <span className="block h-3 w-40 animate-pulse rounded bg-canvas-inset" />
          <span className="block h-3 w-24 animate-pulse rounded bg-canvas-inset" />
        </span>
        <span className="h-3 w-14 animate-pulse rounded bg-canvas-inset" />
      </div>
    ))}
  </div>
);

/* Billing's split of agent usage by person, for the Owner. */
export const PeopleUsagePanel: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<UsagePeriod>('period');
  const [data, setData] = useState<PeopleUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const latest = useRef(0);

  const load = useCallback((chosen: UsagePeriod) => {
    latest.current += 1;
    const request = latest.current;
    setLoading(true);
    setFailed(false);
    billingApi.getPeopleUsage(chosen)
      .then(({ data: next }) => {
        if (request !== latest.current) return;
        setData(next);
      })
      .catch(() => {
        if (request !== latest.current) return;
        setFailed(true);
      })
      .finally(() => {
        if (request !== latest.current) return;
        setLoading(false);
      });
  }, []);

  useEffect(() => { load(period); }, [load, period]);

  const people = data?.people || [];
  const used = people.filter((p) => p.calls > 0 || p.total_usd > 0);
  const idle = people.filter((p) => !(p.calls > 0 || p.total_usd > 0));
  const total = data?.total_usd || 0;
  let listed = used.slice(0, SHOWN);
  if (showAll) listed = [...used, ...idle];
  const hidden = used.length + idle.length - listed.length;

  let body: React.ReactNode = null;
  if (loading && !data) body = <SkeletonList />;
  if (!loading && failed) {
    body = (
      <div className="px-6 pb-6">
        <ErrorNote message="Could not load usage by person." />
        <div className="mt-3"><Action size="sm" variant="outline" onClick={() => load(period)}>Try again</Action></div>
      </div>
    );
  }
  if (!failed && data && !data.available) {
    body = <div className="px-6 pb-6 text-[13px] text-ink-dim">Usage figures are being refreshed. Check back in a minute.</div>;
  }
  if (!failed && data?.available) {
    body = (
      <>
        <div className={`px-6 pb-5 transition-opacity ${loading ? 'opacity-50' : ''}`}>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-[20px] font-bold tabular-nums text-ink">
              {usd(total)}
              <span className="ml-1.5 text-[12.5px] font-medium text-ink-dim">
                {sinceLabel(data.period, data.since)}, from {used.length} {used.length === 1 ? 'person' : 'people'}
              </span>
            </span>
            <span className="text-[12px] tabular-nums text-ink-dim">
              AI {usd(data.ai_usd || 0)} · Compute {usd(data.compute_usd || 0)}
            </span>
          </div>
          {total > 0 && <ShareStrip people={used} total={total} />}
          {total === 0 && <p className="m-0 text-[13px] text-ink-dim">No agent usage {sinceLabel(data.period, data.since)}. Try a longer period.</p>}
        </div>
        {listed.length > 0 && (
          <ul className={`m-0 list-none border-t border-line-soft p-0 transition-opacity ${loading ? 'opacity-50' : ''}`}>
            {listed.map((person) => (
              <PersonRow key={person.key} person={person} onOpen={() => navigate(`${personPath(person)}?period=${period}`)} />
            ))}
          </ul>
        )}
        {(hidden > 0 || showAll) && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line-soft px-6 py-3">
            <span className="text-[12px] text-ink-dim">
              {idle.length > 0 && !showAll && `${idle.length} more ${idle.length === 1 ? 'person has' : 'people have'} no usage yet.`}
            </span>
            <Action size="sm" variant="outline" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show fewer' : `Show everyone (${used.length + idle.length})`}
            </Action>
          </div>
        )}
      </>
    );
  }

  return (
    <Panel>
      <PanelHead
        kicker="Agent usage"
        title="Usage by person"
        desc="How much AI usage each person in your company has, from agents, chats and builds. Open someone to see their usage over time."
      />
      <div className="px-6 pb-4">
        <FilterChips
          tabs={PERIODS.map((p) => ({ id: p.id, label: p.label }))}
          active={period}
          onChange={(id) => setPeriod(id as UsagePeriod)}
          label="Usage period"
        />
      </div>
      {body}
    </Panel>
  );
};
